import React, { useState } from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { LuCheck, LuChevronsUpDown, LuPlus } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/utils/cn';

interface AllocationGroupNameFieldProps<T extends FieldValues> {
    control: Control<T>;
    name: Path<T>;
    allocationGroupOptions: Array<{ value: string; label: string }>;
}

export function AllocationGroupNameField<T extends FieldValues>({
    control,
    name,
    allocationGroupOptions,
}: AllocationGroupNameFieldProps<T>) {
    const [groupNameComboOpen, setGroupNameComboOpen] = useState(false);
    const [groupNameSearch, setGroupNameSearch] = useState('');

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const existingGroupNames = allocationGroupOptions.map((opt) => opt.value);
                const isNewGroup =
                    groupNameSearch.trim() &&
                    !existingGroupNames.some(
                        (groupName) => groupName.toLowerCase() === groupNameSearch.trim().toLowerCase(),
                    );

                return (
                    <FormItem>
                        <FormLabel>Allocation Group</FormLabel>
                        <FormControl>
                            <Popover open={groupNameComboOpen} onOpenChange={setGroupNameComboOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={groupNameComboOpen}
                                        className="w-full justify-between"
                                    >
                                        {field.value || 'Select or create group...'}
                                        <LuChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[400px] p-0" align="start">
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder="Search or type new group name..."
                                            className="h-9"
                                            value={groupNameSearch}
                                            onValueChange={setGroupNameSearch}
                                        />
                                        <CommandList>
                                            {!groupNameSearch && allocationGroupOptions.length === 0 && (
                                                <CommandEmpty>Type to create a new group</CommandEmpty>
                                            )}
                                            {groupNameSearch &&
                                                allocationGroupOptions.filter((opt) =>
                                                    opt.label.toLowerCase().includes(groupNameSearch.toLowerCase()),
                                                ).length === 0 &&
                                                !isNewGroup && <CommandEmpty>No groups found</CommandEmpty>}
                                            {allocationGroupOptions
                                                .filter((opt) =>
                                                    opt.label.toLowerCase().includes(groupNameSearch.toLowerCase()),
                                                )
                                                .map((option) => (
                                                    <CommandItem
                                                        key={option.value}
                                                        value={option.value}
                                                        onSelect={() => {
                                                            field.onChange(option.value);
                                                            setGroupNameComboOpen(false);
                                                            setGroupNameSearch('');
                                                        }}
                                                    >
                                                        <LuCheck
                                                            className={cn(
                                                                'mr-2 h-4 w-4',
                                                                field.value === option.value
                                                                    ? 'opacity-100'
                                                                    : 'opacity-0',
                                                            )}
                                                        />
                                                        {option.label}
                                                    </CommandItem>
                                                ))}
                                            {isNewGroup && (
                                                <CommandItem
                                                    value={groupNameSearch.trim()}
                                                    onSelect={() => {
                                                        field.onChange(groupNameSearch.trim());
                                                        setGroupNameComboOpen(false);
                                                        setGroupNameSearch('');
                                                    }}
                                                    className="text-blue-600"
                                                >
                                                    <LuPlus className="mr-2 h-4 w-4" />
                                                    Create new group: &quot;{groupNameSearch.trim()}&quot;
                                                </CommandItem>
                                            )}
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}
